<script setup>
import ModalWrapper from '@/modals/ModalWrapper';
import { ref, watch } from 'vue';
import { useProgramStore } from '@/stores/program';

const emit = defineEmits(['close-modal']);

const { updateProgramTaskOrder } = useProgramStore();

const props = defineProps({
    program: {
        type: Object,
        required: true,
    },
});

const tasks = ref([]);
const draggingEnabled = ref(false);

watch(() => props.program.tasks, (programTasks) => {
    tasks.value = [...(programTasks ?? [])];
}, { immediate: true });

const dragstart = (index, event) => {
    event.dataTransfer.setData('text/plain', index);
    event.dataTransfer.effectAllowed = 'move';
};

const dragover = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
};

const drop = (newIndex, event) => {
    event.preventDefault();

    const oldIndex = Number(event.dataTransfer.getData('text/plain'));

    if (oldIndex === newIndex) {
        return;
    }

    const reorderedTasks = [...tasks.value];
    const [movedTask] = reorderedTasks.splice(oldIndex, 1);

    reorderedTasks.splice(newIndex, 0, movedTask);
    tasks.value = reorderedTasks;
};

const stopDragging = () => {
    draggingEnabled.value = false;
};

const moveTask = (index, direction) => {
    const newIndex = index + direction;

    if (newIndex < 0 || newIndex >= tasks.value.length) {
        return;
    }

    const reorderedTasks = [...tasks.value];

    // Swap the selected task with the task above or below it.
    [
        reorderedTasks[index],
        reorderedTasks[newIndex],
    ] = [
        reorderedTasks[newIndex],
        reorderedTasks[index],
    ];

    tasks.value = reorderedTasks;
};

const save = async () => {
    await updateProgramTaskOrder({
        program_hash: props.program.hash,
        tasks: tasks.value.map(task => task.hash),
    });

    emit('close-modal', true);
};

const cancel = () => {
    emit('close-modal', false);
};
</script>

<template>
<ModalWrapper>
    <template #header>{{ $t('label.arrange_tasks') }}</template>
    <template #subheader>{{ $t('label.arrange_tasks_description') }}</template>

    <div class="max-h-[50vh] overflow-y-auto pr-2 space-y-2">
        <div
            v-for="(task, index) in tasks"
            :key="task.hash"
            :draggable="draggingEnabled"
            class="flex flex-wrap sm:flex-nowrap items-center gap-3 border border-gray-light rounded p-4 bg-white"
            @dragstart="dragstart(index, $event)"
            @dragover="dragover"
            @drop="drop(index, $event)"
            @dragend="stopDragging">

            <FontAwesomeIcon
                icon="grip"
                class="text-primary cursor-move"
                :title="$t('label.drag_to_reorder')"
                @mousedown="draggingEnabled = true"
                @mouseup="stopDragging" />

            <div
                class="flex shrink-0 items-center justify-center min-w-[90px] rounded bg-gray-100 px-3 py-2 text-sm font-semibold">
                {{ $t('label.order') }} {{ index + 1 }}
            </div>

            <div class="min-w-0 flex-1 font-medium">
                {{ task.name }}
            </div>

            <div class="flex shrink-0 gap-2 ml-auto">
                <v-btn
                    :title="$t('label.move_task_up')"
                    :aria-label="$t('label.move_task_up')"
                    :disabled="index === 0"
                    icon="mdi-arrow-up"
                    size="small"
                    variant="outlined"
                    @click="moveTask(index, -1)" />

                <v-btn
                    :title="$t('label.move_task_down')"
                    :aria-label="$t('label.move_task_down')"
                    :disabled="index === tasks.length - 1"
                    icon="mdi-arrow-down"
                    size="small"
                    variant="outlined"
                    @click="moveTask(index, 1)" />
            </div>
        </div>
    </div>

    <template #footer>
        <StyledSaveButton @save="save" @cancel="cancel" />
    </template>
</ModalWrapper>
</template>
